var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { merge } from 'lodash';
import { getError, getAppInstance, getHeaders, formatError } from './utils';
/**
 * KPI event logger.
 *
 * @example
 * const kpi = new Kpi(payload)
 *
 * // log the beginning of a KPI operation (START)
 * kpi.start(KpiOperation.EXAMPLE_OPERATION)
 *
 * // record an error (string, APIError, DasAPIError)
 * kpi.queueError({
 *   reason: '3DS_FAILED',
 *   details: '3DS failed to launch'
 * })
 *
 * // log the end of a KPI operation (COMPLETED/FAILED)
 * // along with its completed data
 * kpi.end('COMPLETED', {
 *  cardData: {
      cardId: 'GhZnDOtURVyu8pqUCC3Rhg000000000000GB',
    }
 * })
 */
export class Kpi {
    constructor(payload, meta = () => ({})) {
        var _a;
        /** Adapter specific meta data */
        this.meta = () => ({});
        /** List of KPI error logs. */
        this.errors = [];
        /** KPI queue */
        this.queue = [];
        /** List of remote logger events. */
        this.remoteLogs = [];
        /** Post request headers */
        this.headers = {};
        /**
         * Builds common KPI data fields for all events
         */
        this.getCommonData = () => {
            var _a, _b, _c;
            return {
                srciData: {
                    srciId: this.payload.srcClientId
                },
                dpaData: {
                    dpaId: (_a = this.payload.authenticationContext) === null || _a === void 0 ? void 0 : _a.srcDpaId,
                    name: (_c = (_b = this.payload.authenticationContext) === null || _b === void 0 ? void 0 : _b.dpaData) === null || _c === void 0 ? void 0 : _c.dpaName
                },
                cardData: {
                    cardId: this
                        .payload
                        .accountReference
                        .srcDigitalCardId,
                    cardVerificationData: {
                        cardAuthRequestReason: this
                            .payload
                            .authenticationContext
                            .authenticationReasons[0]
                    }
                }
            };
        };
        /**
         * Log the beginning of a KPI operation (START)
         */
        this.start = (operation) => {
            this.operation = operation;
            this.log(operation, 'STARTED', merge(this.getCommonData(), this.meta(), getAppInstance()));
        };
        /**
         * Log the end of a KPI operation (COMPLETED/FAILED)
         */
        this.end = (state, result = {}) => {
            const error = this.errors.pop();
            const adapterPayload = state === 'FAILED'
                ? formatError(error)
                : result;
            if (!this.operation)
                return;
            this.log(this.operation, state, merge(this.getCommonData(), this.meta(), adapterPayload, getAppInstance()));
            this.errors = [];
        };
        /**
         * Records an error.
         */
        this.queueError = (error) => {
            const payload = getError(error);
            this.errors.push(payload);
            this.remoteLogs.push({
                level: 'ERROR',
                event: payload.reason,
                details: {
                    message: payload.message,
                    timestamp: new Date(Date.now()).toUTCString()
                }
            });
        };
        /** Logs the given payload. */
        this.log = (operation, state, payload) => {
            this.queue.push({
                data: Object.assign({}, payload),
                state,
                operation
            });
        };
        /**
         * Aggregates the queue and POST xhr
         */
        this.flush = ({ queue, remoteLogs }) => __awaiter(this, void 0, void 0, function* () {
            try {
                const finalQueue = this.queue.concat(queue);
                if (finalQueue.length > 0) {
                    const finalPayload = finalQueue.map(payload => ({
                        data: JSON.parse(JSON.stringify(payload.data)),
                        state: payload.state,
                        operation: payload.operation,
                        namespace: this.namespace,
                        source: this.source,
                        resource: 'CARD',
                        tags: ['kpi']
                    }));
                    yield Promise.all(finalPayload
                        .map(this.postKPIData)
                        .concat(this.getRemoteLoggerRequest(remoteLogs)));
                    this.queue = [];
                    this.remoteLogs = [];
                }
            }
            catch (_) {
                // Do nothing
            }
        });
        /**
         * Returns a new remotelogger request.
         */
        this.getRemoteLoggerRequest = (remoteLogs) => __awaiter(this, void 0, void 0, function* () {
            if (remoteLogs.length === 0) {
                return Promise.resolve();
            }
            yield axios.post('/api/logging/remotelog', {
                logs: this.remoteLogs.concat(remoteLogs),
                source: this.source,
                version: 'beta'
            }, {
                headers: this.headers
            });
        });
        this.postKPIData = (data) => __awaiter(this, void 0, void 0, function* () {
            yield axios.post('/api/event/publish', data, { headers: this.headers });
        });
        this.headers = getHeaders(payload);
        this.meta = meta;
        this.payload = payload;
        this.namespace = `src.kpi.${payload.namespace}`;
        this.source = JSON.stringify({
            domain: `MA_${(_a = payload.namespace) === null || _a === void 0 ? void 0 : _a.toUpperCase()}`,
            version: 'beta',
            environment: 'stage'
        });
    }
}
//# sourceMappingURL=Kpi.js.map